import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, useMemo } from "react";
import RenderCategoryItems from "./renderCategoryItems";
import CategoryOrder from "./categoryOrder";
import CategoryShowMap from "./categoryShowMap";
const CategoryCard = ({ categories, renters, rate, totalDays, currency, extraFilters, filters, setFilters, setLoading, categoryTranslations, setSortBy, setSortOrder, sortOrder, showFilters, isSearchCompleted, isFinal, isComplete, onFilterClick, collections = [], }) => {
    /**
     * Determines if all category and renter filters have finished loading their data.
     *
     * Combines the `categories` and `renters` arrays, then checks if every item in the combined array
     * does not have the `loadingData` flag set to `true`.
     *
     * @returns {boolean} `true` if all filters have finished loading data, otherwise `false`.
     *
     * @dependency categories - Array of category filter objects, each possibly containing a `loadingData` property.
     * @dependency renters - Array of renter filter objects, each possibly containing a `loadingData` property.
     */
    const isCompleteFilters = useMemo(() => {
        const allFilters = [
            ...categories,
            ...renters,
        ];
        return allFilters.every((item) => !item.loadingData);
    }, [categories, renters]);
    // AB#8231 — categories finalize on the deterministic `isFinal` backend signal:
    // FilterResult.IsFinal=true means the orchestrator is complete AND every provider
    // has responded for this search, so a price===0 category is empty for real (no
    // late arrival will rescue it) and can be hidden. Until then, empty pills keep
    // showing the loader — no 1s grace timer is needed because the signal itself is
    // the grace.
    //
    // Safety net (Bug #8051): if the backend never emits IsFinal=true (older deploys,
    // network drop, backend bug), `forcedFinal` flips to true 30s after mount so
    // loader pills never stay stuck forever. The 30s budget is intentionally generous
    // — far longer than the ~3-8s typical end-to-end search — so the safety net never
    // fires during a healthy search and misclassifies a late provider as "empty".
    //
    // AB#8269 — the symmetric failure mode (backend emits IsFinal=true PREMATURELY
    // after a currency-change remount, before the new-currency prices have hydrated)
    // is guarded upstream:
    //   1. `SearchResults.tsx` clears `sessionStorage.searchId` and resets
    //      `isFinal=false` on a confirmed currency change, AND gates `setIsFinal(true)`
    //      in `processFilters` to require at least one priced car.
    //   2. `renderCategoryItems.tsx` adds a 2s post-mount all-zero defense-in-depth
    //      guard that renders loaders instead of collapsing pills when every item
    //      has price=0 within the recent-mount window.
    // The 30s `forcedFinal` safety net below MUST be preserved — it is the only
    // safeguard for the Bug #8051 path (backend never emits IsFinal=true at all).
    const [forcedFinal, setForcedFinal] = useState(false);
    useEffect(() => {
        const safety = setTimeout(() => setForcedFinal(true), 30000);
        return () => clearTimeout(safety);
    }, []);
    const effectiveIsFinal = Boolean(isFinal) || forcedFinal;
    // AB#8534 followup (2026-05-13) — pair `isComplete` to the same 30s
    // `forcedFinal` safety net. `renderCategoryItems.tsx` state 4
    // (Empty-definitive, return null) requires BOTH `isFinal && isComplete`;
    // without this pairing, when the safety net flips `effectiveIsFinal=true`
    // at 30s the FE enters state 3 (Pending-bypass-active) — a spinner — for
    // pills with `price===0` because `effectiveIsComplete = isComplete !==
    // false = false !== false = false`. Those pills then render an infinite
    // loader until the component unmounts. Stage 2026-05-13 repro: searchId
    // 6a0457244019255a1989f873 — "Eléctrico" car category and "Payless"
    // renter pills (both `price:0, quantity:0` across all WS frames, with no
    // provider emitting `isComplete:true`) stuck in loader for the full
    // session.
    //
    // The pairing matches the comment-documented intent of WI #8534's
    // `renderCategoryItems` 4-state machine — "callers wiring through the
    // post-#8421 signal pass `isComplete=true` alongside it" — but the
    // safety-net branch was the missed wiring. The companion BookingEngine
    // PR (`include EarlyExitSlices in IsFinal quorum`) now emits `IsFinal=
    // true` earlier (when the orchestrator closes with compensation slices),
    // so the FE typically reaches state 4 well before 30s when the BE fix is
    // also deployed. The 30s safety net remains as the last-resort guarantee
    // that loaders never stick forever.
    const effectiveIsComplete = Boolean(isComplete) || forcedFinal;
    const containerRef = useRef(null);
    const mainGroupRef = useRef(null);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    // AB#8323 — gate the initial applyScroll() so setHasScrolled(false) only
    // fires once on first mount. Each WS FilterResult flips isCompleteFilters,
    // which previously re-armed the scroll/animation cycle on every event.
    const applyScrollDoneRef = useRef(false);
    useEffect(() => {
        if (!showFilters)
            return;
        const container = containerRef.current;
        const mainGroup = mainGroupRef.current;
        if (!container || !mainGroup)
            return;
        const applyScroll = () => {
            // AB#8323 — only run once. Subsequent calls (driven by isCompleteFilters
            // flipping on each WebSocket FilterResult) must be ignored to avoid the
            // setHasScrolled(false) reset that re-triggered the auto-scroll loop.
            if (applyScrollDoneRef.current)
                return;
            setHasScrolled(false);
            if (mainGroup.style.left !== "") {
                applyScrollDoneRef.current = true;
                return; // Avoid applying scroll if already set
            }
            applyScrollDoneRef.current = true;
        };
        // Delay until next frame to ensure layout is complete
        const raf = requestAnimationFrame(() => {
            applyScroll();
        });
        return () => cancelAnimationFrame(raf);
    }, [showFilters, isCompleteFilters]);
    useEffect(() => {
        function handleResize() {
            setWindowWidth(window.innerWidth);
        }
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    const checkScrollable = () => {
        if (containerRef.current) {
            const container = containerRef.current;
            const contentWidth = container.scrollWidth;
            const containerWidth = container.clientWidth;
            const currentScroll = container.scrollLeft;
            setShowLeftArrow(currentScroll > 0);
            // Solo ocultamos la flecha derecha si hasScrolled es true
            if (hasScrolled) {
                setShowRightArrow(Math.ceil(currentScroll + containerWidth) < contentWidth);
            }
        }
    };
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            window.addEventListener("resize", checkScrollable);
            container.addEventListener("scroll", checkScrollable);
        }
        return () => {
            if (container) {
                container.removeEventListener("scroll", checkScrollable);
            }
            window.removeEventListener("resize", checkScrollable);
        };
    }, [hasScrolled]);
    // AB#8323 — depend on lengths (primitives) instead of array references.
    // Each WebSocket FilterResult delivers fresh array refs even when the
    // structural content is unchanged, which previously re-fired this effect on
    // every WS event and contributed to the auto-scroll re-arm cycle.
    useEffect(() => {
        const timer = setTimeout(checkScrollable, 100);
        return () => clearTimeout(timer);
    }, [filters, categories === null || categories === void 0 ? void 0 : categories.length, renters === null || renters === void 0 ? void 0 : renters.length, extraFilters === null || extraFilters === void 0 ? void 0 : extraFilters.length]);
    const handleInteraction = () => {
        if (!hasScrolled) {
            setHasScrolled(true);
        }
        checkScrollable();
    };
    const handleSelect = (selectedCategory, type) => {
        selectedCategory.selected = !selectedCategory.selected;
        setLoading(true);
        const filterMap = {
            renter: "renterFilters",
            category: "categoryFilters",
            fuel: "engineFilters",
            rate: "rateFilters",
        };
        const filterType = filterMap[type];
        if (filterType) {
            const filterUpdate = filters[filterType] || [];
            const filterCurrent = filterUpdate.includes(selectedCategory.value)
                ? []
                : [selectedCategory.value];
            const filterClear = Object.entries(filters).reduce((acc, [key, value]) => {
                if (key === "PickUpLocationCode" || key === "ReturnLocationCode") {
                    acc[key] = "";
                }
                else {
                    acc[key] = Array.isArray(value) ? [] : value;
                }
                return acc;
            }, {});
            // WI #8534 — arm the click-cut barrier BEFORE `setFilters` so the next
            // WS master frame (which may race past in the same React tick) is
            // dropped if it does not match the new filter signature. The signature
            // mirrors the single-select semantics of `setFilters` below:
            // selecting one value (or empty if deselecting) per filter axis.
            const nextSignature = {
                renters: type === "renter" ? filterCurrent : [],
                typesCar: type === "category" ? filterCurrent : [],
                fuelTypes: type === "fuel" ? filterCurrent : [],
                groupRates: type === "rate" ? filterCurrent : [],
            };
            onFilterClick === null || onFilterClick === void 0 ? void 0 : onFilterClick(nextSignature);
            setFilters({
                ...filterClear,
                [filterType]: filterCurrent,
            });
        }
    };
    const scrollContainer = (direction) => {
        if (containerRef.current) {
            containerRef.current.scrollLeft += direction * 480;
            if (!hasScrolled) {
                setHasScrolled(true);
            }
            checkScrollable();
        }
    };
    const convertPriceMemoized = (price) => {
        return price;
    };
    /*const fuelItems = () => {
  
      return extraFilters.filter(category => category.type === "fuel").map((category, index) => (
        <div
          className={`categories__item ${filters.engineFilters.includes(category.value) ? "categories__item--active" : ""}`}
          key={index}
          onClick={() => { handleSelect(category, "fuel"); }}
          id={"filter" + category.value}
          category-tittle={category.value == "R" ? "Fuel" : "Electric"}
        >
          <CategoryContent image={category.icon} name={category.name} price={convertPriceMemoized(category.price)} currency={currency} isCompleted={category.loadingData}/>
        </div>
      ));
    };*/
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: `scroll-arrows left ${!showLeftArrow ? "hidden" : ""}`, onClick: () => scrollContainer(-1) }), _jsx("div", { className: "categories__degree categories__degree--left" }), _jsx("div", { className: "categories__container", id: "categoriesContainer", ref: containerRef, onClick: handleInteraction, onScroll: handleInteraction, children: _jsxs("div", { ref: mainGroupRef, className: `categories-main__group ${hasScrolled ? "no-negative-margin" : ""}`, children: [_jsx("div", { className: "categories__group", children: _jsx(CategoryOrder, { setSortBy: setSortBy, setSortOrder: setSortOrder, value: "Amount", sortOrder: sortOrder }) }), _jsx("div", { className: "categories__group", children: _jsx(CategoryShowMap, { filters: filters, setFilters: setFilters }) }), _jsx("div", { className: "categories__group", children: _jsx(RenderCategoryItems, { items: categories, type: "category", convertPriceMemoized: convertPriceMemoized, handleSelect: handleSelect, translations: categoryTranslations, currency: currency, filters: filters, isFinal: effectiveIsFinal, isComplete: effectiveIsComplete }) }), _jsx("div", { className: "categories__group", children: _jsx(RenderCategoryItems, { items: renters, type: "renter", convertPriceMemoized: convertPriceMemoized, handleSelect: handleSelect, currency: currency, filters: filters, isFinal: effectiveIsFinal, isComplete: effectiveIsComplete, collections: collections }) })] }) }), _jsx("div", { className: `scroll-arrows right ${!showRightArrow && hasScrolled ? "hidden" : ""}`, onClick: () => scrollContainer(1) }), _jsx("div", { className: "categories__degree categories__degree--right" })] }));
};
export default React.memo(CategoryCard);
